<template>
  <div id="accordionTourComplementary" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTourComplementary" class="accordion-header">
        <button
          aria-controls="collapseTourComplementary"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseTourComplementary"
          data-bs-toggle="collapse"
          type="button">
          <div class="mb-0 d-flex">
            <div class="bg-secondary rounded-xs h-fit-content me-8">
              <v-icon icon="contact" size="xxl" space="p-12 text-white"/>
            </div>

            <div>
              <div class="h4 ff-secondary mb-0">{{ $t('clientTour.complementaryText') }}</div>
              <div class="fw-light accordion-subtitle">{{ $t('clientTour.complementarySubtitle') }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTourComplementary"
        aria-labelledby="headingTourComplementary"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTourComplementary">
        <div class="accordion-body pb-32 pb-xl-48">
          <form
            id="case-tour-update-complementary-text-form"
            class="d-flex flex-column justify-content-between h-100"
          >
            <fieldset>
              <legend class="visually-hidden">{{ $t('clientTour.complementaryConditions') }}</legend>

              <div class="form-outline">
                <label class="label">
                  <span>{{ $t('clientTour.complementaryText') }}</span>
                </label>

                <v-html-editor
                  id="case-tour-update-complementary-text"
                  ref="case-tour-update-complementary-text-ref"
                  :value="complementaryLocal"
                  @input="complementaryLocal = $event"
                  @media-uploaded="editorMedia.push($event)"
                />
              </div>
            </fieldset>

            <v-button
              :disabled="!meta.valid || disableButton || !meta.dirty"
              :is-loading="accepted"
              class="btn-icon w-fit align-self-end mt-24"
              size="sm"
              variant="primary"
              @click="updateTour"
            >
              {{ $t('general.button.save') }}
              <v-icon icon="arrow-right" size="sm" space="ms-12"/>
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';
import { useField } from 'vee-validate';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VHtmlEditor from '@/components/vendor/htmlEditor/VHtmlEditor.vue';
import api from '@/api';
import useFormValidation from '@/helpers/form';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import ClientTour from '@/api/objects/ClientTour';

export default defineComponent({
  name: 'TheCaseSetupTourComplementary',
  components: {
    VHtmlEditor,
    VIcon,
    VButton,
  },
  emits: ['reloadClientTour'],
  props: {
    agency: {
      type: String,
      required: true,
    },
    clientTour: {
      type: Object as PropType<ClientTour>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();

    const rules = yup.object({
      complementaryLocal: yup.string()
        .nullable()
        .label(t('clientTour.complementaryText')),
    });

    const initialValues = {
      complementaryLocal: props.clientTour.complementaryText,
    };

    const form = useFormValidation(rules, initialValues);

    const { value: complementaryLocal } = useField<string>('complementaryLocal');

    return {
      ...form,
      complementaryLocal,
    };
  },
  data() {
    return {
      disableButton: false as boolean,
      editorMedia: [] as any[],
    };
  },
  methods: {
    async updateTour() {
      try {
        this.toggleAccepted();
        this.disableButton = true;

        const data = {
          client_id: this.clientTour.clientId,
          init_date: this.clientTour.initDate,
          end_date: this.clientTour.endDate,
          pax_number: this.clientTour.paxNumber,
          type: this.clientTour.type,
          terms_conditions_text: this.clientTour.termsConditionsText,
          complementary_text: this.complementaryLocal,
        };

        await api.case.update(this.agency, this.clientTour.id, data);

        if (!!this.editorMedia && this.editorMedia.length > 0) {
          const editorMedia = {
            media: this.editorMedia,
            model_type: 'client_tour',
            collection: 'tinymce',
            model_id: this.clientTour.id,
          };

          await api.media.confirm('s3', editorMedia);
        }

        this.$toast.success(this.$t('general.shared.savedChanges'));

        this.$emit('reloadClientTour');

        this.disableButton = false;
        this.toggleAccepted();
      } catch (e: any) {
        this.disableButton = false;
        this.toggleAccepted();

        console.error(e);

        this.$toast.error(e.response.data.message);
      }
    },
  },
});
</script>
